/**
 * ForgotPasswordModal - Modal per il reset password
 * Implementa il flow completo di password reset per mobile
 */

import React, { useState, useCallback } from 'react';
import './ForgotPasswordModal.css';
import { useAuthContext } from '../../../context/AuthContext';

const ForgotPasswordModal = ({ isOpen, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [emailSent, setEmailSent] = useState(false);
  const [touchedEmail, setTouchedEmail] = useState(false);

  const { forgotPassword, error: authError, clearError } = useAuthContext();

  // Validate email
  const validateEmail = useCallback((emailValue) => {
    if (!emailValue) {
      return 'Email è richiesta';
    }
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(emailValue)) {
      return 'Formato email non valido';
    }
    return '';
  }, []);

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // Clear errors when user types
    if (errors.email) {
      setErrors(prev => ({ ...prev, email: '' }));
    }
    if (authError) {
      clearError();
    }
  };

  const handleEmailBlur = () => {
    setTouchedEmail(true);
    const emailError = validateEmail(email);
    setErrors(prev => ({ ...prev, email: emailError }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLoading) return;

    // Validate email
    const emailError = validateEmail(email);
    if (emailError) {
      setErrors({ email: emailError });
      setTouchedEmail(true);
      return;
    }

    setIsLoading(true);
    setErrors({});
    clearError();

    try {
      console.log('[ForgotPasswordModal] Sending password reset request for:', email);
      
      const response = await forgotPassword(email);
      
      console.log('[ForgotPasswordModal] Password reset request successful:', response);
      
      // Show success state
      setEmailSent(true);
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess(email);
      }
      
    } catch (error) {
      console.error('[ForgotPasswordModal] Password reset failed:', error);
      
      // Handle specific error cases
      if (error.message && error.message.includes('rate limit')) {
        setErrors({ 
          general: 'Troppi tentativi. Riprova tra qualche minuto.' 
        });
      } else {
        setErrors({ 
          general: error.message || 'Errore durante l\'invio. Riprova.' 
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset all state when closing
    setEmail('');
    setErrors({});
    setEmailSent(false);
    setTouchedEmail(false);
    setIsLoading(false);
    clearError();
    onClose();
  };

  const handleBackToReset = () => {
    setEmailSent(false);
    setErrors({});
    clearError();
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="forgot-password-modal-overlay" onClick={handleClose}>
      <div className="forgot-password-modal" onClick={(e) => e.stopPropagation()}>
        
        {!emailSent ? (
          // Email Input Form
          <>
            <div className="forgot-password-header">
              <button 
                className="forgot-password-close"
                onClick={handleClose}
                aria-label="Chiudi"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            <div className="forgot-password-content">
              <div className="forgot-password-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.9 1 3 1.9 3 3V19C3 20.1 3.9 21 5 21H11V19H5V3H13V7H17V9H19V11H21V9ZM13 13V17H15V19H13C12.4 19 12 18.6 12 18V13H10L13 10L16 13H13Z" fill="currentColor"/>
                </svg>
              </div>

              <h2 className="forgot-password-title">Password Dimenticata?</h2>
              <p className="forgot-password-description">
                Inserisci il tuo indirizzo email e ti invieremo un link per reimpostare la password.
              </p>

              {(errors.general || authError) && (
                <div className="forgot-password-error mobile-error-message">
                  <span>{errors.general || authError}</span>
                  <button 
                    type="button" 
                    className="mobile-clear-error-button" 
                    onClick={() => {
                      setErrors(prev => ({ ...prev, general: '' }));
                      clearError();
                    }}
                    title="Chiudi messaggio"
                  >
                    ×
                  </button>
                </div>
              )}

              <form onSubmit={handleSubmit} className="forgot-password-form">
                <div className="mobile-form-field">
                  <div className={`mobile-input-container mobile-floating-label ${email ? 'has-value' : ''} ${errors.email ? 'error' : ''}`}>
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={handleEmailChange}
                      onBlur={handleEmailBlur}
                      className="mobile-form-input"
                      placeholder=" "
                      autoComplete="email"
                      required
                      disabled={isLoading}
                    />
                    <label className="mobile-floating-label-text">Indirizzo Email</label>
                    {email && !errors.email && (
                      <div className="mobile-input-icon success">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  {errors.email && touchedEmail && (
                    <span className="mobile-error-message">{errors.email}</span>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !email}
                  className={`mobile-submit-button forgot-password-submit ${isLoading ? 'loading' : ''}`}
                >
                  {isLoading ? (
                    <div className="mobile-loading-spinner"></div>
                  ) : (
                    'Invia Link di Reset'
                  )}
                </button>
              </form>

              <div className="forgot-password-back">
                <button 
                  type="button" 
                  onClick={handleClose}
                  className="forgot-password-back-btn"
                >
                  ← Torna al Login
                </button>
              </div>
            </div>
          </>
        ) : (
          // Email Sent Success State
          <>
            <div className="forgot-password-header">
              <button 
                className="forgot-password-close"
                onClick={handleClose}
                aria-label="Chiudi"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            <div className="forgot-password-content">
              <div className="forgot-password-icon success">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              <h2 className="forgot-password-title">Email Inviata!</h2>
              <p className="forgot-password-description">
                Abbiamo inviato un link per il reset della password a:
              </p>
              <p className="forgot-password-email">{email}</p>
              <p className="forgot-password-description small">
                Controlla la tua casella email e segui le istruzioni per reimpostare la password. Se non vedi l'email, controlla la cartella spam.
              </p>

              <div className="forgot-password-actions">
                <button
                  type="button"
                  onClick={handleBackToReset}
                  className="forgot-password-resend-btn"
                >
                  Invia di Nuovo
                </button>
                <button 
                  type="button" 
                  onClick={handleClose}
                  className="mobile-submit-button"
                >
                  Torna al Login
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordModal;